import { observer } from 'mobx-react'
import { ChangeEvent, useCallback } from 'react'
import styled from 'styled-components'
import { ChecklistKind } from 'models/Checklists/ChecklistKind'
import type { ExistingChecklist } from 'models/Checklists/ChecklistModel'
import { NewVehicleEditCloneModel, VehicleEditClone } from 'models/Vehicles/VehicleModel'
import { Fieldset } from '../Form/Form'
import { TextInput } from '../Input/Input'
import { Select, SelectOption, SelectOptions } from '../Select/Select'
import { Column } from '../Table/Table'
import { BoldText } from '../Typography/Typography'

const VehicleDetailsContainer = styled(Fieldset)`
    grid-template-columns: 1fr 1fr;
`

interface VehicleDetailsProps {
    vehicle: VehicleEditClone
    centerChecklists: Array<ExistingChecklist>
}

const NO_CHECKLIST_OPTION: SelectOption = { value: 'None', text: 'None' }
const checklistAsSelectOption = (checklist: ExistingChecklist | null) => {
    return checklist ? { value: checklist.id.toString(), text: checklist.name } : NO_CHECKLIST_OPTION
}
export const VehicleDetails = observer(({ vehicle, centerChecklists } : VehicleDetailsProps) => {
    const getChecklistsSelectOptions = useCallback((checklistKind: ChecklistKind): SelectOptions => {
        return [NO_CHECKLIST_OPTION, ...centerChecklists.filter(checklist => checklist.checklistKind === checklistKind).map(checklistAsSelectOption)] as const
    }, [])
    const isNewVehicle = NewVehicleEditCloneModel.is(vehicle)
    return (
        <VehicleDetailsContainer>
            <Column gap="1.5em">
                <BoldText>Information</BoldText>
                <TextInput {...{
                    name: 'id',
                    label: 'Id',
                    type: 'number',
                    isDisabled: !isNewVehicle,
                    value: vehicle.id?.toString() ?? '',
                    ...(isNewVehicle && { 
                        onChange: ({ target: {value}} : ChangeEvent<HTMLInputElement>) => {
                            const valueAsNumber = parseInt(value)
                            vehicle.setId(isNaN(valueAsNumber) ? null : valueAsNumber)
                    }})
                }} />
                <TextInput {...{
                    name: 'name',
                    label: 'Nickname',
                    value: vehicle.name ?? '',
                    onChange: ({ target: {value}} : ChangeEvent<HTMLInputElement>) => {
                        vehicle.setName(value)
                    }
                }}/> 
                <TextInput {...{
                    name: 'Registration number',
                    label: 'Registration number',
                    value: vehicle.registrationNumber ?? '',
                    onChange: ({ target: {value}} : ChangeEvent<HTMLInputElement>) => {
                        vehicle.setRegistrationNumber(value)
                    }
                }}/>
            </Column>
            <Column gap="1.5em" style={{gridColumnStart: "2"}}>
                <BoldText>Checklists</BoldText>
                <Select {...{
                    name: 'Day open checklist',
                    label: 'Day open checklist',
                    options: getChecklistsSelectOptions(ChecklistKind.LaunchpadDayOpen),
                    selectedOptions: [checklistAsSelectOption(vehicle.launchpadOpenChecklist)],
                    onChange: ({ target: { value } }: ChangeEvent<HTMLSelectElement>) => {
                        if (value === NO_CHECKLIST_OPTION.value) {
                            vehicle.setLaunchpadOpenChecklist(null)    
                        } else {
                            const selectedChecklistId = parseInt(value)
                            const checklist = centerChecklists.find(checklist => checklist.id == selectedChecklistId) ?? null
                            vehicle.setLaunchpadOpenChecklist(checklist)
                        }
                    },
                }}/>
                <Select {...{
                    name: 'Day close checklist',
                    label: 'Day close checklist',
                    options: getChecklistsSelectOptions(ChecklistKind.LaunchpadDayClose),
                    selectedOptions: [checklistAsSelectOption(vehicle.launchpadCloseChecklist)],
                    onChange: ({ target: { value } }: ChangeEvent<HTMLSelectElement>) => {
                        if (value === NO_CHECKLIST_OPTION.value) {
                            vehicle.setLaunchpadCloseChecklist(null)    
                        } else {
                            const selectedChecklistId = parseInt(value)
                            const checklist = centerChecklists.find(checklist => checklist.id == selectedChecklistId) ?? null
                            vehicle.setLaunchpadCloseChecklist(checklist)
                        }
                    },
                }}/>
                <Select {...{
                    name: 'Preflight checklist',
                    label: 'Preflight checklist',
                    options: getChecklistsSelectOptions(ChecklistKind.VehiclePreflight),
                    selectedOptions: [checklistAsSelectOption(vehicle.preflightChecklist)],
                    onChange: ({ target: { value } }: ChangeEvent<HTMLSelectElement>) => {
                        if (value === NO_CHECKLIST_OPTION.value) {
                            vehicle.setPreflightChecklist(null)    
                        } else {
                            const selectedChecklistId = parseInt(value)
                            const checklist = centerChecklists.find(checklist => checklist.id == selectedChecklistId) ?? null
                            vehicle.setPreflightChecklist(checklist)
                        }
                    },
                }}/>
                <Select {...{
                    name: 'Postflight checklist',
                    label: 'Postflight checklist',
                    options: getChecklistsSelectOptions(ChecklistKind.VehiclePostflight),
                    selectedOptions: [checklistAsSelectOption(vehicle.postflightChecklist)],
                    onChange: ({ target: { value } }: ChangeEvent<HTMLSelectElement>) => {
                        if (value === NO_CHECKLIST_OPTION.value) {
                            vehicle.setPostflightChecklist(null)    
                        } else {
                            const selectedChecklistId = parseInt(value)
                            const checklist = centerChecklists.find(checklist => checklist.id == selectedChecklistId) ?? null
                            vehicle.setPostflightChecklist(checklist)
                        }
                    },
                }}/>
                <Select {...{
                    name: 'Vehicle Preparation checklist',
                    label: 'Vehicle Preparation checklist',
                    options: getChecklistsSelectOptions(ChecklistKind.VehiclePrepare),
                    selectedOptions: [checklistAsSelectOption(vehicle.preparationChecklist)],
                    onChange: ({ target: { value } }: ChangeEvent<HTMLSelectElement>) => {
                        if (value === NO_CHECKLIST_OPTION.value) {
                            vehicle.setPreparationChecklist(null)    
                        } else {
                            const selectedChecklistId = parseInt(value)
                            const checklist = centerChecklists.find(checklist => checklist.id == selectedChecklistId) ?? null
                            vehicle.setPreparationChecklist(checklist)
                        }
                    },
                }}/>
            </Column>
        </VehicleDetailsContainer>
    )
})